import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Segmented } from 'antd';
import Axios from 'axios';
import moment from 'moment';

import ls from 'local-storage';
import styled from 'styled-components';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import CenteredBlock from '@components/Gui/CenteredBlock';
import CustomSwitch from '@components/Gui/CustomSwitch';
import EmptyState from '@components/Gui/EmptyState';
import { Loading } from '@components/Gui/Loading';
import NwDrawer from '@components/Gui/NwDrawer';
import TransactionDetailDrawer from '@components/Transactions/TransactionsDrawer/TransactionDetailDrawer';

import { SearchResultList, StatementSearchResultsContainer } from '@containers/Accounting/Billing/BillingCommonUI';
import BillingInfoSearchGrandTotal from '@containers/Accounting/Billing/Common/BillingInfoSearchGrandTotal';
import BillingInfoSearchItem from '@containers/Accounting/Billing/Common/BillingInfoSearchItem';

const DrawerContent = styled.div`
    .drawer-summary {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 1rem;
        align-items: flex-start;
        padding: 1rem 1.5rem;
        margin-bottom: 1rem;
        background: #fff;
        border: 1px solid #f0f0f0;
        border-radius: 6px;
    }

    .drawer-summary-scope {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        width: 100%;

        h2 {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 600;
        }

        p {
            margin: 0;
            color: ${(props) => props.theme.TextColorMuted};
            text-transform: uppercase;
            font-size: ${(props) => props.theme.FontSizeSm};
            letter-spacing: 0.02em;
        }
    }

    .drawer-summary-filters {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
        margin-top: 0.75rem;
    }

    .drawer-summary-filters-left,
    .drawer-summary-filters-right {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        min-width: 0;
    }

    .drawer-summary-filters-right {
        justify-content: flex-end;
    }

    .drawer-summary-filter-group {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        min-width: 0;
    }

    .drawer-summary-filter-label {
        color: ${(props) => props.theme.TextColorMuted};
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: 500;
        letter-spacing: 0.02em;
        line-height: 1;
        text-transform: uppercase;
    }

    .drawer-summary-filter-switches {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        align-items: center;
    }

    .drawer-summary-status-switch {
        font-size: ${(props) => props.theme.FontSizeSm};
        font-weight: 400;
        text-transform: uppercase;
    }

    .drawer-error {
        margin-bottom: 1rem;
    }

    @media (max-width: 960px) {
        .drawer-summary-filters {
            flex-direction: column;
        }

        .drawer-summary-filters-left,
        .drawer-summary-filters-right {
            width: 100%;
            justify-content: flex-start;
            flex-wrap: wrap;
        }
    }
`;

const SEARCH_FROM_DATE = '1900-01-01';
const ACTIVE_FAMILY = 'CommAsMothAg';
const PASSIVE_FAMILY = 'JobFee';
const MODE_AS_MOTHER_AGENCY = 'asMotherAgency';
const MODE_TO_MOTHER_AGENCY = 'toMotherAgency';
const LEGACY_MODE_MAP = {
    active: MODE_AS_MOTHER_AGENCY,
    passive: MODE_TO_MOTHER_AGENCY,
};
const VALID_MODES = [MODE_AS_MOTHER_AGENCY, MODE_TO_MOTHER_AGENCY];
const COMMISSIONS_DRAWER_FILTERS_KEY = 'nw_config_commissionsdrawer_filters';
const COMMISSIONS_DRAWER_CLIENT_STATUS_LEGACY_KEY = 'nw_config_commissionsdrawer_clientstatus';
const PASSIVE_CLIENT_STATUS_OPTIONS = ['option', 'job', 'billable', 'locked', 'invoiced'];
const FILTER_SEGMENTED_OPTIONS = ['all', 'yes', 'no'];

const getSearchToDate = () => moment().add(1, 'year').format('YYYY-MM-DD');

const getDefaultDrawerFilters = () => ({
    clientStatus: [...PASSIVE_CLIENT_STATUS_OPTIONS],
    paidByClient: 'all',
    paid: 'all',
});

const normalizeDrawerFilters = (storedFilters) => {
    const defaultFilters = getDefaultDrawerFilters();
    const storedPaidValue = storedFilters?.paid ?? storedFilters?.paidToAgency;

    return {
        clientStatus: Array.isArray(storedFilters?.clientStatus)
            ? PASSIVE_CLIENT_STATUS_OPTIONS.filter((status) => storedFilters.clientStatus.includes(status))
            : defaultFilters.clientStatus,
        paidByClient: FILTER_SEGMENTED_OPTIONS.includes(storedFilters?.paidByClient)
            ? storedFilters.paidByClient
            : defaultFilters.paidByClient,
        paid: FILTER_SEGMENTED_OPTIONS.includes(storedPaidValue) ? storedPaidValue : defaultFilters.paid,
    };
};

const getStoredDrawerFilters = () => {
    const storedFilters = ls.get(COMMISSIONS_DRAWER_FILTERS_KEY);
    const legacyClientStatus = ls.get(COMMISSIONS_DRAWER_CLIENT_STATUS_LEGACY_KEY);

    if (storedFilters) {
        return normalizeDrawerFilters(storedFilters);
    }

    if (legacyClientStatus) {
        return normalizeDrawerFilters({ clientStatus: legacyClientStatus });
    }

    return getDefaultDrawerFilters();
};

const getBooleanFilterValue = (value) => {
    if (value === 'yes') {
        return true;
    }

    if (value === 'no') {
        return false;
    }

    return undefined;
};

const normalizeMode = (mode) => {
    const mappedMode = LEGACY_MODE_MAP[mode] || mode;
    return VALID_MODES.includes(mappedMode) ? mappedMode : MODE_AS_MOTHER_AGENCY;
};

const buildSearchPayload = ({ agencyId, modelId, mode, drawerFilters }) => {
    const paid = getBooleanFilterValue(drawerFilters?.paid);
    const basePayload = {
        DatePeriod: {
            From: SEARCH_FROM_DATE,
            To: getSearchToDate(),
        },
        ContactID: agencyId,
    };

    if (mode === MODE_AS_MOTHER_AGENCY) {
        return {
            ...basePayload,
            Families: [ACTIVE_FAMILY],
            ClientStatus: Array.isArray(drawerFilters?.clientStatus)
                ? drawerFilters.clientStatus
                : [...PASSIVE_CLIENT_STATUS_OPTIONS],
            ...(paid !== undefined ? { Paid: paid } : {}),
            ...(modelId ? { ModelFilters: { ModelID: modelId } } : {}),
        };
    }

    const paidByClient = getBooleanFilterValue(drawerFilters?.paidByClient);

    return {
        ...basePayload,
        Families: [PASSIVE_FAMILY],
        ClientStatus: Array.isArray(drawerFilters?.clientStatus)
            ? drawerFilters.clientStatus
            : [...PASSIVE_CLIENT_STATUS_OPTIONS],
        ExcludeWithoutClientStatement: paidByClient !== undefined,
        ...(paidByClient !== undefined ? { ClientFilters: { Paid: paidByClient } } : {}),
        ...(paid !== undefined ? { Paid: paid } : {}),
        ...(modelId ? { ModelFilters: { ModelID: modelId } } : {}),
    };
};

const buildFallbackBillingInfo = (statement, billingInfoID) => ({
    ID: billingInfoID,
    CompanyName: statement.BillingInfoCompanyName || statement.CompanyName || `Billing Info #${billingInfoID}`,
    InvoiceType: statement.BillingInfoInvoiceType || statement.InvoiceType || '',
    City: statement.BillingInfoCity || statement.City || '',
    Country: statement.BillingInfoCountry || statement.Country || '',
    PaymentTermCode: statement.PaymentTermCode,
});

const groupTransactionsByBillingInfo = ({ transactions, agencyId, agencyName, billingInfosMap, mode }) => {
    if (!Array.isArray(transactions) || transactions.length === 0) {
        return [];
    }

    const groupedByBillingInfo = transactions.reduce((acc, transaction) => {
        const transactionStatements = Array.isArray(transaction.Statements) ? transaction.Statements : [];
        const mainJobFeeStatement =
            transaction.Family === PASSIVE_FAMILY
                ? transactionStatements.find((statement) => !statement.isCommission && !statement.isClientAdvance)
                : null;

        transactionStatements.forEach((statement) => {
            const billingInfoID = statement.BillingInfoID;

            if (!billingInfoID) {
                return;
            }

            if (!acc[billingInfoID]) {
                acc[billingInfoID] = {
                    ContactID: agencyId,
                    ContactName: agencyName,
                    BillingInfoID: billingInfoID,
                    BillingInfo:
                        billingInfosMap.get(billingInfoID) || buildFallbackBillingInfo(statement, billingInfoID),
                    Statements: [],
                };
            }

            acc[billingInfoID].Statements.push({
                ...statement,
                Transaction: { ...transaction },
                ...(mode === MODE_TO_MOTHER_AGENCY && mainJobFeeStatement
                    ? {
                          JobFeeAmount: mainJobFeeStatement.Amount ?? 0,
                          JobFeeAmountDef: mainJobFeeStatement.AmountDef ?? 0,
                      }
                    : {}),
            });
        });

        return acc;
    }, {});

    return Object.values(groupedByBillingInfo)
        .map((group) => ({
            ...group,
            Statements: [...group.Statements].sort((left, right) => {
                const leftDate = new Date(left.RefDate || left.Transaction?.RefDate || 0).getTime();
                const rightDate = new Date(right.RefDate || right.Transaction?.RefDate || 0).getTime();
                return rightDate - leftDate;
            }),
        }))
        .sort((left, right) =>
            (left.BillingInfo?.CompanyName || '').localeCompare(right.BillingInfo?.CompanyName || '', undefined, {
                sensitivity: 'base',
            })
        );
};

const CommissionsDrawer = ({
    agencyId,
    idagency,
    agencyName,
    modelId,
    idmodel,
    modelName,
    mode = MODE_AS_MOTHER_AGENCY,
    onClose,
}) => {
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [transactionWasModified, setTransactionWasModified] = useState(false);
    const [drawerFilters, setDrawerFilters] = useState(getStoredDrawerFilters);

    const resolvedAgencyId = agencyId || idagency;
    const resolvedModelId = modelId || idmodel;
    const resolvedMode = normalizeMode(mode);
    const resolvedAgencyName = agencyName || getContactName(resolvedAgencyId) || `Agency #${resolvedAgencyId}`;
    const resolvedModelName = modelName || (resolvedModelId ? getContactName(resolvedModelId) : null);
    const showPaidByClientFilter = resolvedMode === MODE_TO_MOTHER_AGENCY;
    const paidFilterLabel = resolvedMode === MODE_TO_MOTHER_AGENCY ? 'Paid to agency' : 'Paid';

    const modeLabel =
        resolvedMode === MODE_AS_MOTHER_AGENCY ? 'Commissions As Mother Agency' : 'Mother Agency Commissions';
    const statementType = resolvedMode === MODE_AS_MOTHER_AGENCY ? 'Client' : 'Agency';

    useEffect(() => {
        ls.set(COMMISSIONS_DRAWER_FILTERS_KEY, drawerFilters);
    }, [drawerFilters]);

    const { data: billingInfos = [] } = useQuery({
        queryKey: ['contact-billinginfos', resolvedAgencyId],
        queryFn: () => Axios.get(`/contacts/billinginfos/${resolvedAgencyId}`),
        enabled: Boolean(resolvedAgencyId),
        staleTime: 30000,
    });

    const {
        data: transactions = [],
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ['commissions-drawer', resolvedMode, resolvedAgencyId, resolvedModelId, drawerFilters],
        queryFn: () =>
            Axios.post(
                resolvedMode === MODE_AS_MOTHER_AGENCY ? '/statements/client/search' : '/statements/agency/search',
                buildSearchPayload({
                    agencyId: resolvedAgencyId,
                    modelId: resolvedModelId,
                    mode: resolvedMode,
                    drawerFilters,
                })
            ),
        enabled: Boolean(resolvedAgencyId),
        staleTime: 30000,
    });

    const billingInfosMap = useMemo(
        () => new Map((billingInfos || []).map((billingInfo) => [billingInfo.ID, billingInfo])),
        [billingInfos]
    );

    const statementGroups = useMemo(
        () =>
            groupTransactionsByBillingInfo({
                transactions,
                agencyId: resolvedAgencyId,
                agencyName: resolvedAgencyName,
                billingInfosMap,
                mode: resolvedMode,
            }),
        [transactions, resolvedAgencyId, resolvedAgencyName, billingInfosMap, resolvedMode]
    );

    const handleReload = () => {
        refetch();
    };

    const handleEditTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setTransactionWasModified(false);
    };

    const markTransactionAsModified = () => {
        setTransactionWasModified(true);
    };

    const handleCloseTransactionDetail = () => {
        setSelectedTransaction(null);
        if (transactionWasModified) {
            handleReload();
            setTransactionWasModified(false);
        }
    };

    const handleToggleClientStatus = (status, checked) => {
        const nextClientStatus = checked
            ? PASSIVE_CLIENT_STATUS_OPTIONS.filter(
                  (item) => item === status || drawerFilters.clientStatus.includes(item)
              )
            : drawerFilters.clientStatus.filter((item) => item !== status);

        setDrawerFilters({
            ...drawerFilters,
            clientStatus: nextClientStatus,
        });
    };

    return (
        <NwDrawer title="Commissions" width="72rem" minWidth="960px" whiteBg={false} onClose={onClose}>
            <DrawerContent>
                <div className="drawer-summary">
                    <div className="drawer-summary-scope">
                        <p>{modeLabel}</p>
                        <h2>
                            {resolvedModelName ? `${resolvedAgencyName} / ${resolvedModelName}` : resolvedAgencyName}
                        </h2>
                        {VALID_MODES.includes(resolvedMode) && (
                            <div className="drawer-summary-filters">
                                <div className="drawer-summary-filters-left">
                                    <div className="drawer-summary-filter-group">
                                        <div className="drawer-summary-filter-label">Status</div>
                                        <div className="drawer-summary-filter-switches">
                                            {PASSIVE_CLIENT_STATUS_OPTIONS.map((status) => (
                                                <CustomSwitch
                                                    key={status}
                                                    className="drawer-summary-status-switch"
                                                    size="small"
                                                    value={drawerFilters.clientStatus.includes(status)}
                                                    onChange={(checked) => handleToggleClientStatus(status, checked)}
                                                    text={status.charAt(0).toUpperCase() + status.slice(1)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="drawer-summary-filters-right">
                                    {showPaidByClientFilter && (
                                        <div className="drawer-summary-filter-group">
                                            <div className="drawer-summary-filter-label">Paid by client</div>
                                            <Segmented
                                                size="small"
                                                options={[
                                                    { label: 'All', value: 'all' },
                                                    { label: 'Yes', value: 'yes' },
                                                    { label: 'No', value: 'no' },
                                                ]}
                                                value={drawerFilters.paidByClient}
                                                onChange={(value) =>
                                                    setDrawerFilters({
                                                        ...drawerFilters,
                                                        paidByClient: value,
                                                    })
                                                }
                                            />
                                        </div>
                                    )}
                                    <div className="drawer-summary-filter-group">
                                        <div className="drawer-summary-filter-label">{paidFilterLabel}</div>
                                        <Segmented
                                            size="small"
                                            options={[
                                                { label: 'All', value: 'all' },
                                                { label: 'Yes', value: 'yes' },
                                                { label: 'No', value: 'no' },
                                            ]}
                                            value={drawerFilters.paid}
                                            onChange={(value) =>
                                                setDrawerFilters({
                                                    ...drawerFilters,
                                                    paid: value,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <Alert className="drawer-error" type="error" showIcon message="Unable to load commissions." />
                )}

                {isLoading ? (
                    <CenteredBlock fullpage headerHeight={260}>
                        <Loading loadingType="centered" />
                    </CenteredBlock>
                ) : statementGroups.length === 0 ? (
                    <CenteredBlock fullpage headerHeight={260}>
                        <EmptyState
                            message={
                                resolvedMode === MODE_AS_MOTHER_AGENCY
                                    ? 'No commissions as mother agency found'
                                    : 'No mother agency commissions found'
                            }
                        />
                    </CenteredBlock>
                ) : (
                    <SearchResultList>
                        <StatementSearchResultsContainer>
                            {statementGroups.map((group) => (
                                <div
                                    className="subject-container"
                                    key={`billinginfo-${resolvedMode}-${group.BillingInfoID}`}
                                >
                                    <BillingInfoSearchItem
                                        contact={group}
                                        statementType={statementType}
                                        onEditTransaction={handleEditTransaction}
                                        readOnly
                                        selectionEnabled={false}
                                        showBothStatuses={resolvedMode === MODE_TO_MOTHER_AGENCY}
                                        showTableHeader
                                        singleCellDetails
                                        statementDirection={
                                            resolvedMode === MODE_AS_MOTHER_AGENCY ? 'receivable' : 'payable'
                                        }
                                        showJobFeeAmount={resolvedMode === MODE_TO_MOTHER_AGENCY}
                                    />
                                </div>
                            ))}
                            {/* <BillingInfoSearchGrandTotal contacts={statementGroups} /> */}
                        </StatementSearchResultsContainer>
                    </SearchResultList>
                )}
            </DrawerContent>

            {selectedTransaction && (
                <TransactionDetailDrawer
                    hideProject
                    transaction={selectedTransaction}
                    onClose={handleCloseTransactionDetail}
                    onUpdateTransaction={markTransactionAsModified}
                    onTransactionRemoved={markTransactionAsModified}
                />
            )}
        </NwDrawer>
    );
};

export default CommissionsDrawer;
